
import { mapState } from 'vuex';
import cartMixin from '@/mixins/cartMixin';
import addressMixin from '@/mixins/addressMixin';
import checkoutPaymentMixin from '@/mixins/checkoutPaymentMixin';
import PayzenPayment from '@/services/payments/PayzenPayment';

export default {
  name: 'PayzenForm',
  mixins: [cartMixin, addressMixin, checkoutPaymentMixin],
  data() {
    return {
      formToken: null,
      publicKey: process.env.PAYZEN_PUBLIC_KEY,
      paymentService: null,
      isInitialized: false,
      loading: true,
      submitLoading: false,
      isInitializing: false,
    };
  },
  computed: {
    ...mapState('checkout', ['carrierLoading', 'submittingPayment']),
    currentLocale() {
      return this.$i18n?.locale || 'fr';
    },
    cartAmount() {
      return this.cart?.Total?.ToPay?.TaxIncl;
    },
    cartCarrierId() {
      return this.cart?.Shipping?.Carrier?.IdCarrier;
    },
  },
  created() {
    this.paymentService = new PayzenPayment({
      cart: this.cart,
      auth: this.$auth,
      http: this.$http,
      addressService: this.addressService,
    });
    this._KR = null;
  },
  mounted() {
    this.loadKRScript();
    this.addFixPositionStyle();
  },
  methods: {
    addFixPositionStyle() {
      const style = document.createElement('style');
      style.textContent = `
        .kr-embedded.kr-out-of-view-form, 
        .kr-embedded.kr-out-of-view-form[krvue], 
        .kr-embedded.kr-out-of-view-form[krvue][kr-popin], 
        .kr-smart-form.kr-out-of-view-form, 
        .kr-smart-form.kr-out-of-view-form[krvue], 
        .kr-smart-form.kr-out-of-view-form[krvue][kr-popin] {
          position: static !important;
          top: auto !important;
          left: auto !important;
          right: auto !important;
          bottom: auto !important;
        }
      `;
      document.head.appendChild(style);
    },
    loadKRScript() {
      return new Promise((resolve, reject) => {
        this.loading = true;
        if (typeof KR !== 'undefined') {
          this.initializePayzen();
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src =
          'https://static.payzen.eu/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js';
        script.async = true;
        script.onload = () => {
          const style = document.createElement('link');
          style.rel = 'stylesheet';
          style.href =
            'https://static.payzen.eu/static/js/krypton-client/V4.0/ext/neon-reset.min.css';
          document.head.appendChild(style);
          setTimeout(() => {
            this.initializePayzen();
            resolve();
          }, 300);
        };
        script.onerror = () => {
          this.error = this.$t('Failed to load payment form');
          this.loading = false;
          reject();
        };
        document.head.appendChild(script);
      });
    },
    async initializePayzen() {
      if (this.isInitialized || this.isInitializing) {
        this.loading = false;
        return;
      }
      this.isInitializing = true;
      try {
        if (typeof KR === 'undefined') {
          this.error = this.$t('Payment system not loaded');
          this.loading = false;
          return;
        }
        try {
          this.formToken = await this.paymentService.formToken({
            isDigital: this.isDigitalCart,
          });
          if (!this.formToken) {
            this.error = this.$t('Failed to get payment token');
            this.loading = false;
            return;
          }
        } catch (tokenError) {
          console.error('Error getting form token:', tokenError);
          this.error = this.$t('Failed to get payment token');
          this.loading = false;
          return;
        }
        const config = {
          formToken: this.formToken,
          'kr-language': this.currentLocale,
          'kr-public-key': this.publicKey,
          'kr-theme': 'classic',
          'kr-placeholder-pan': '4970 1000 0000 0000',
          'kr-placeholder-expiry': 'MM/YY',
          'kr-placeholder-security-code': '123',
          'kr-hide-debug-message': true,
        };
        try {
          const result = await KR.setFormConfig(config);
          this._KR = result.KR;

          // If we were already initialized, we only needed setFormConfig
          // calling addForm or onSubmit again would break things
          if (this.isInitialized) {
            this.loading = false;
            return;
          }

          // Clear container to avoid CLIENT_706 warning (content deleted on addForm)
          const container = document.getElementById('payzenForm');
          if (container) container.innerHTML = '';

          await this._KR.addForm('#payzenForm');
          this.$nextTick(() => {
            const container = document.getElementById('payzenForm');
            if (container) {
              container.addEventListener(
                'click',
                async (e) => {
                  const button = e.target.closest('.kr-payment-button');
                  if (button) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    await this.submitPayment();
                  }
                },
                true
              );
            }
          });
          this._KR.onSubmit((event) => {
            const orderDetails = event?.clientAnswer?.orderDetails;
            const orderId = orderDetails?.orderId;

            if (orderId) {
              this.toggleLoader(true);
              this.addPaymentInfoGtm('Card - PayZen');
              this.$router.push({
                path: this.localePath(
                  `/cart/thanks/accepted?orderid=${orderId}&cartid=${this.cartService.idCart}`
                ),
              });
              this.$emit('payment-submitted', event);
            }
            this.submitLoading = false;
          });
          this._KR.onError((event) => {
            this.error = event.errorMessage;
            this.$emit('payment-error', event);
            this.toggleLoader(false);
            this.submitLoading = false;
            this.$store.commit('checkout/setSubmittingPayment', false);
          });
          this.isInitialized = true;
          setTimeout(() => {
            this.loading = false;
          }, 500);
        } catch (initError) {
          console.error('PayZen initialization error:', initError);
          this.error = this.$t('Failed to initialize payment form');
          this.loading = false;
        } finally {
          this.isInitializing = false;
        }
      } catch (err) {
        console.error('PayZen setup error:', err);
        this.error = this.$t(
          'An error occurred while setting up the payment form'
        );
        this.loading = false;
      }
    },
    async submitPayment() {
      if (this.submitLoading || this.submittingPayment) return;
      this.$store.commit('checkout/setSubmittingPayment', true);

      try {
        // Ensure form is initialized
        if (!this.isInitialized && !this.isInitializing) {
          await this.initializePayzen();
        }

        // If still initializing (from watcher or this call), wait for it
        if (this.isInitializing) {
          this.submitLoading = true;
          try {
            while (this.isInitializing) {
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          } finally {
            this.submitLoading = false;
          }
        }

        if (!this._KR) {
          this.error = this.$t('Payment form not initialized');
          this.$store.commit('checkout/setSubmittingPayment', false);
          return;
        }

        // If carrier is still being saved, we need to wait
        if (this.carrierLoading) {
          this.submitLoading = true;
          try {
            while (this.carrierLoading) {
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          } finally {
            this.submitLoading = false;
          }
        }

        if (!this.checkFormOk()) {
          this.$store.commit('checkout/setSubmittingPayment', false);
          return;
        }

        const saved = await this.ensureAddressesSaved();
        if (!saved) {
          this.$store.commit('checkout/setSubmittingPayment', false);
          return;
        }

        this.submitLoading = true;

        // Re-check if addresses saving triggered a re-init
        if (this.isInitializing) {
          this.submitLoading = true;
          try {
            while (this.isInitializing) {
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          } finally {
            this.submitLoading = false;
          }
        }

        // I'll check if the cart address changed
        await this._KR.submit();
      } catch (err) {
        console.error('Payment submission error:', err);
        this.error = this.$t('Failed to submit payment');
        this.submitLoading = false;
        this.$store.commit('checkout/setSubmittingPayment', false);
      } finally {
        // Note: we don't reset submittingPayment here because 
        // redirects or onSubmit will handle it if successful.
        // But if we're here, it failed or something is wrong.
      }
    },
    toggleLoader(val) {
      this.$store.commit('shared/wind/setIsLoading', val);
    },
    handleAddressUpdate(val) {
      if (val && !this.isInitialized && !this.isInitializing) {
        this.error = null;
        this.initializePayzen();
      }
    },
  },
  watch: {
    '$store.state.checkout.shippingAddress': 'handleAddressUpdate',
    '$store.state.checkout.invoiceAddress': 'handleAddressUpdate',
    cartAmount: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal && oldVal !== undefined) {
          // If already submitting, don't fully reset isInitialized
          // but we still want to refresh the token
          this.initializePayzen();
        }
      },
    },
    cartCarrierId: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal && oldVal !== undefined) {
          this.initializePayzen();
        }
      },
    },
  },
  beforeDestroy() {
    this.$store.commit('checkout/setSubmittingPayment', false);
  },
};
